<script setup>
import { computed, ref, watch } from 'vue';
import { format } from 'date-fns';
import {
  getWeeksForMonth,
  isToday,
  monthName,
  yearName,
} from './helpers/DatePickerHelper';
import CalendarAction from './components/CalendarAction.vue';
import CalendarWeekLabel from './components/CalendarWeekLabel.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  blockedDates: { type: Array, default: () => [] },
  maxDaysAhead: { type: Number, default: 30 },
});
const emit = defineEmits(['update:modelValue']);
const currentMonth = ref(
  new Date(
    `${props.modelValue || new Date().toISOString().slice(0, 10)}T12:00:00`
  )
);
const open = ref(false);
const weeks = computed(() => getWeeksForMonth(currentMonth.value));
const selectedDate = computed(() => props.modelValue);
const dateKey = date => format(date, 'yyyy-MM-dd');
const isBlocked = date => {
  const key = dateKey(date);
  return props.blockedDates.some(blocked => {
    const base = new Date(`${blocked.date}T12:00:00`);
    if (blocked.recurrence === 'weekly') return base.getDay() === date.getDay();
    if (blocked.recurrence === 'monthly')
      return base.getDate() === date.getDate();
    if (blocked.recurrence === 'yearly') {
      return (
        base.getMonth() === date.getMonth() && base.getDate() === date.getDate()
      );
    }
    return dateKey(base) === key;
  });
};
const isOutsideBookingWindow = date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lastAllowed = new Date(today);
  lastAllowed.setDate(lastAllowed.getDate() + props.maxDaysAhead);
  const candidate = new Date(date);
  candidate.setHours(0, 0, 0, 0);
  return candidate < today || candidate > lastAllowed;
};
const selectDate = date => {
  if (
    date.getMonth() !== currentMonth.value.getMonth() ||
    isBlocked(date) ||
    isOutsideBookingWindow(date)
  )
    return;
  emit('update:modelValue', dateKey(date));
  open.value = false;
};
const moveMonth = offset => {
  currentMonth.value = new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() + offset,
    1,
    12
  );
};
watch(
  () => props.modelValue,
  value => {
    if (value) currentMonth.value = new Date(`${value}T12:00:00`);
  }
);
</script>

<template>
  <div class="relative">
    <button
      type="button"
      class="mt-1 flex w-full items-center justify-between rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-left text-sm text-n-slate-12"
      @click="open = !open"
    >
      <span>{{ selectedDate || 'dd/mm/aaaa' }}</span>
      <i class="i-lucide-calendar" />
    </button>
    <div
      v-if="open"
      v-on-clickaway="() => (open = false)"
      class="absolute left-0 top-full z-40 mt-1 w-[330px] rounded-xl bg-n-alpha-3 p-4 shadow-xl outline outline-1 outline-n-container backdrop-blur-[100px]"
    >
      <CalendarAction
        calendar-type="start"
        :first-button-label="monthName(currentMonth)"
        :button-label="yearName(currentMonth)"
        view-mode="month"
        @prev="moveMonth(-1)"
        @next="moveMonth(1)"
      />
      <CalendarWeekLabel />
      <div
        v-for="week in weeks"
        :key="week[0].getTime()"
        class="grid grid-cols-7 gap-2"
      >
        <button
          v-for="day in week"
          :key="day.getTime()"
          type="button"
          class="relative flex h-8 items-center justify-center rounded-lg text-sm font-medium"
          :class="{
            'text-n-slate-10': day.getMonth() !== currentMonth.getMonth(),
            'text-n-slate-12 hover:bg-n-alpha-2':
              day.getMonth() === currentMonth.getMonth() && !isBlocked(day),
            'cursor-not-allowed text-n-slate-10 line-through': isBlocked(day),
            'cursor-not-allowed text-n-slate-10 opacity-50':
              isOutsideBookingWindow(day),
            'bg-n-brand text-white': dateKey(day) === selectedDate,
            'outline outline-1 outline-n-blue-8':
              isToday(new Date(), day) && dateKey(day) !== selectedDate,
          }"
          :disabled="
            day.getMonth() !== currentMonth.getMonth() ||
            isBlocked(day) ||
            isOutsideBookingWindow(day)
          "
          @click="selectDate(day)"
        >
          {{ day.getDate() }}
        </button>
      </div>
    </div>
  </div>
</template>
